import { getItem, removeItem, setItem } from '@alltrails/session-storage/utils';
import type WebList from '@alltrails/types/WebList';

/**
 * The list a user most recently saved an item to, persisted for the current browser session.
 * Powers the "one tap" save flow: the first save of a session defaults to Favorites, and every
 * subsequent save auto-targets whatever list was last saved to.
 *
 * The value is scoped to the user id it was written for. This matters on shared tabs: session
 * storage survives sign-out, and some list ids (e.g. Favorites, which is 1000 for every account)
 * exist for every user — so without scoping, the next account could auto-save to the previous
 * user's target. On a user mismatch we treat it as absent.
 */
const LAST_SAVED_LIST_KEY = 'oneTapLastSavedList';

type StoredLastSavedList = {
  userId: number;
  list: WebList;
};

export const getLastSavedList = (userId: number): WebList | null => {
  const stored = getItem<StoredLastSavedList>(LAST_SAVED_LIST_KEY);
  if (!stored || stored.userId !== userId) {
    return null;
  }
  return stored.list;
};

export const setLastSavedList = (userId: number, list: WebList) => setItem<StoredLastSavedList>(LAST_SAVED_LIST_KEY, { userId, list });

export const clearLastSavedList = () => removeItem(LAST_SAVED_LIST_KEY);
